/* FieldWrapper — container base para campos de formulário.
   Centraliza label + description + erro acessível. Os campos controlados
   em @planuze/ui/form consomem este wrapper.

   SEMPRE renderiza o `<div>` container — alternar entre fragment e div
   quando o estado de erro muda desmonta/remonta o filho e quebra o foco
   em campos com validação live. */
import type { ReactNode } from 'react';
import { Label } from '../components/label.js';
import { cn } from '../lib/utils.js';

export interface FieldWrapperProps {
  /** Nome do campo — usado em htmlFor + data-testid. */
  readonly name: string;
  /** Label visível acima. */
  readonly label?: ReactNode | undefined;
  /** Classe extra no label visível. */
  readonly labelClassName?: string | undefined;
  /** Texto explicativo abaixo do label. */
  readonly description?: string | undefined;
  /** Mensagem de erro de validação controlada pelo consumer. */
  readonly error?: string | undefined;
  /** Mensagem de aviso não-bloqueante — só renderiza quando `error` está
   *  ausente. Útil para hints como "use snake_case". */
  readonly warning?: string | undefined;
  /** Marca campo obrigatório com `*` destructive. */
  readonly required?: boolean | undefined;
  /** Classe extra no container raiz. */
  readonly className?: string | undefined;
  /** Reserva altura do slot de erro mesmo quando ausente — evita reflow
   *  ao validar live e mantém alinhamento horizontal entre campos em grids.
   *  Default `true` (padrão Planuze: todo campo reserva o slot). Use `false`
   *  apenas em layouts compactos onde o salto vertical de 16px não importa
   *  (ex.: forms inline de 1 linha). */
  readonly reserveErrorSlot?: boolean | undefined;
  readonly children: ReactNode;
}

export function FieldWrapper({
  name,
  label,
  labelClassName,
  description,
  error,
  warning,
  required,
  className,
  reserveErrorSlot = true,
  children,
}: FieldWrapperProps) {
  const hasLabel = label !== undefined && (typeof label !== 'string' || label !== '');
  const hasDescription = description !== undefined && description !== '';
  const hasError = error !== undefined && error !== '';
  const hasWarning = !hasError && warning !== undefined && warning !== '';
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)} data-testid={`field-${name}`}>
      {hasLabel && (
        <Label
          htmlFor={name}
          className={cn(
            'text-foreground/80 text-xs font-semibold',
            hasError && 'text-destructive',
            labelClassName,
          )}
        >
          {label}
          {required === true && (
            <span className="text-destructive ml-1" aria-hidden="true">
              *
            </span>
          )}
        </Label>
      )}

      {hasDescription && (
        <p className="text-muted-foreground text-xs leading-snug">{description}</p>
      )}

      {children}

      {hasError ? (
        <p
          id={`${name}-error`}
          role="alert"
          aria-live="polite"
          className="text-destructive block text-xs leading-4"
        >
          {error}
        </p>
      ) : hasWarning ? (
        <p
          id={`${name}-warning`}
          role="status"
          aria-live="polite"
          className="text-warning block text-xs leading-4"
        >
          {warning}
        </p>
      ) : reserveErrorSlot ? (
        /* Slot vazio com altura IDÊNTICA à de 1 linha do `<p>` de
           erro/warning: `text-xs leading-4` = 16px → `block h-4`. Zero
           layout shift entre os estados error → ok → warning enquanto a
           mensagem couber em 1 linha (caso comum). */
        <span aria-hidden="true" className="block h-4" />
      ) : null}
    </div>
  );
}
